import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import pickBy from 'lodash/pickBy';
import {
    FormErrors,
    getFormAsyncErrors,
    getFormSyncErrors,
    hasSubmitSucceeded,
    isInvalid,
    isPristine,
    isSubmitting,
} from 'redux-form';
import { CheckoutFormSection, CHECKOUT_FORM_NAME } from '../constants';
import { PaymentMethodType } from '../models/paymentMethods/enums';
import { CheckoutState } from '../state';
import {
    getActiveFieldsPerSectionNamespace,
    hasErrorInFields,
} from '../utils/forms';
import { isTosClickWrapVisible } from './app';
import { canRetrySubmit, isPaidOrder } from './order';
import {
    getIsPaymentMethodInfoMissing,
    getSelectedPaymentMethod,
} from './payment';

export const isFormSubmitting = (state: CheckoutState, formName: string) =>
    isSubmitting(formName)(state);

export const isFormSuccessfullySubmitted = (
    state: CheckoutState,
    formName: string,
) => hasSubmitSucceeded(formName)(state);

export const isFormSubmitted = (state: CheckoutState, formName: string) =>
    isFormSubmitting(state, formName) ||
    isFormSuccessfullySubmitted(state, formName);

export const isSubmitDisabled = (
    state: CheckoutState,
    formName: string,
    disableOnSucceeded = true,
) =>
    isPristine(formName)(state) ||
    isFormSubmitting(state, formName) ||
    (disableOnSucceeded && isFormSuccessfullySubmitted(state, formName)) ||
    isInvalid(formName)(state);

export const getRegisteredFields = (state: CheckoutState) =>
    Object.keys(state.form[CHECKOUT_FORM_NAME]?.registeredFields ?? {});

export const isCheckoutFormSubmitDisabled = (state: CheckoutState) => {
    // If submit is disabled (loading) this takes precedence over any other condition
    if (isSubmitDisabled(state, CHECKOUT_FORM_NAME)) {
        return true;
    }

    // If the submit can be retried then the button must be enabled so we can't disable it
    if (canRetrySubmit(state)) {
        return false;
    }

    // The remaining conditions are payment-related, which make no sense if it isn't a paid order
    if (!isPaidOrder(state)) {
        return false;
    }

    return getIsPaymentMethodInfoMissing(state);
};

export const getKeepDisabledStyleWhileLoading = (
    state: CheckoutState,
    formName: string,
) =>
    !isTosClickWrapVisible(state) &&
    isSubmitDisabled(state, formName) &&
    !isFormSubmitting(state, formName) &&
    !isFormSuccessfullySubmitted(state, formName);

const getContactInfoError = (
    fields: Record<string, any>,
    syncErrors: FormErrors<{}, string>,
    asyncErrors: FormErrors<{}, string>,
) =>
    hasErrorInFields(
        fields.buyer,
        get(syncErrors, 'buyer'),
        get(asyncErrors, 'buyer'),
    )
        ? [CheckoutFormSection.contactInfo]
        : [];

const getToSError = (
    fields: Record<string, any>,
    errors: FormErrors<{}, string>,
) =>
    !!get(fields, 'tosCheckbox') && !!get(errors, 'tosCheckbox')
        ? [CheckoutFormSection.tosCheckbox]
        : [];

const getAttendeesError = (errors: FormErrors<{}, string>) => {
    const attendeeSurveysErrors = pickBy(errors, (_, key) =>
        key.startsWith('A-'),
    );
    return Object.values(attendeeSurveysErrors).some((attendeeSurvey) =>
        Object.values(attendeeSurvey).some((field) => !!field),
    )
        ? [CheckoutFormSection.attendees]
        : [];
};

const getPaymentError = (
    fields: Record<string, any>,
    syncErrors: FormErrors<{}, string>,
    asyncErrors: FormErrors<{}, string>,
    state: CheckoutState,
) => {
    if (!isPaidOrder(state)) {
        return [];
    }

    return getSelectedPaymentMethod(state) === PaymentMethodType.NOT_SELECTED ||
        hasErrorInFields(
            fields.payment,
            get(syncErrors, 'payment'),
            get(asyncErrors, 'payment'),
        )
        ? [CheckoutFormSection.payment]
        : [];
};

export const getCheckoutFormSectionsWithError = (state: CheckoutState) => {
    const formSyncErrors = getFormSyncErrors(CHECKOUT_FORM_NAME)(state);
    const formAsyncErrors = getFormAsyncErrors(CHECKOUT_FORM_NAME)(state);
    const transformedFields = getActiveFieldsPerSectionNamespace(
        state.form.checkoutForm?.registeredFields,
    );

    if ((!formSyncErrors && !formAsyncErrors) || isEmpty(transformedFields)) {
        return [];
    }

    return [
        ...getContactInfoError(
            transformedFields,
            formSyncErrors,
            formAsyncErrors,
        ),
        ...getAttendeesError(formSyncErrors),
        ...getToSError(transformedFields, formSyncErrors),
        ...getPaymentError(
            transformedFields,
            formSyncErrors,
            formAsyncErrors,
            state,
        ),
    ];
};

export const isFieldRegistered = (state: CheckoutState, fieldName: string) =>
    get(state.form[CHECKOUT_FORM_NAME]?.registeredFields, fieldName)?.count > 0;
